<template>
  <div class="row">
    <div :class="`save-toolbar action-row ${toolbarSize}`">
      <button v-if="primaryEnabled"
        :type="primaryButtonType"
        :disabled="isPrimaryButtonDisabled"
        :class="`btn btn-${primaryStyle}`"
        @click="emitPrimaryClick($event)">
        <FontAwesomeIcon v-if="primaryButtonIcon" :icon="primaryButtonIcon" aria-hidden="true" />
        {{ primaryButtonText }}
        <FontAwesomeIcon v-if="isSavableStateProcessing" :icon="['fas', 'fa-spinner']" class="fa-spin" />
      </button>
      <button v-if="secondaryEnabled"
        :type="secondaryButtonType"
        :disabled="isSecondaryButtonDisabled"
        :class="`btn btn-${secondaryStyle} mr-3`"
        @click="emitSecondaryClick($event)">
        <FontAwesomeIcon v-if="secondaryButtonIcon" :icon="secondaryButtonIcon" aria-hidden="true" />
        {{ secondaryButtonText }}
      </button>

      <div class="float-right ml-auto mr-0" v-if="tertiaryEnabled">
        <slot name="tertiary-button-area">
          <!--
            For the class the mr-0 is needed because we float to the right and the btn adds
            a margin right that we don't want
          -->
          <button
            :type="tertiaryButtonType"
            :disabled="isTertiaryButtonDisabled"
            :class="`btn btn-${tertiaryStyle}`"
            @click="emitTertiaryClick($event)">
            <FontAwesomeIcon v-if="tertiaryButtonIcon" :icon="tertiaryButtonIcon" aria-hidden="true" />
            {{ tertiaryButtonText }}
          </button>
        </slot>
      </div>
      <div v-if="showMessage" class="alert message-component d-inline-block mt-sm-1 mt-md-0"
        :class="{ 'alert-danger': currentState === SaveStates.Errored, 'alert-success': currentState === SaveStates.Success, 'alert-secondary': currentState === SaveStates.Info}"
        role="alert">
        <button type="button" class="close" aria-label="Close" @click="clearState">
          <FontAwesomeIcon :icon="['fas', 'fa-times']" aria-hidden="true" />
        </button>

        {{ currentMessage }}
        <slot name="message" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * This component provides a standard way to manage form buttons (save, cancel, etc) and any messages related
 * to those actions.
 *
 * Provides three buttons labelled primary, secondary and tertiary. All all options are available for all three
 * with different defaults.
 *
 * Overall Props (These affect the whole bar):
 *  - componentState {string <enum SaveStates> } Current state of the component. Understands: Blank (default), Processing, Info, Success and Errored. This
 *                                               state is used to handle message type and style to show and if Processing disables all buttons.
 *                                               Default SaveStates.Blank
 *  - successMessage {string} Message to show when componentState is success. Default "Saved successfully!"
 *  - errorMessage {string} Message to show when componentState is Errored. Default "An error occurred!"
 *  - infoMessage {string} Message to show when componentState is Info. Default ""
 *  - toolbarSize {string} css colSize for the width of the toolbar, default col-12
 *  - primaryButtonIcon, secondaryButtonIcon, tertiaryButtonIcon {Array} Adds FontAwesome Icons to the button, default null
 *
 * Per button props. All options are as props in the format typeOption, e.g. primaryEnabled.
 * Options available:
 *  - Enabled {boolean} Show the button. Defaults [true, true, false]
 *  - ButtonText {string} What text to display in the button. Defaults [Save, Cancel, Info]
 *  - Type {string (submit | button)} Type of button to render. It's an enum, if doesn't match submit, it will render as button. Defaults: [submit, button, button]
 *  - Style {string} Button CSS class without the btn prefix. Defaults [success, outline-secondary, primary]
 *  - Disabled {boolean}  Individually disable buttons. componentState of Processing overrides them all. Defaults [false, false, false]
 *
 * Events:
 *  - primaryClick
 *  - secondaryClick
 *  - tertiaryClick
 *
 */
import { computed, watch, ButtonHTMLAttributes } from 'vue';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
// @ts-ignore
import { SaveStates } from "@/src/enums";
import { useSavableState } from '@/src/composables/useSavableState';

type ActionToolbarProps = {
  componentState?: string | null,
  successMessage?: string,
  errorMessage?: string,
  infoMessage?: string,
  toolbarSize?: string,
  primaryEnabled?: boolean,
  secondaryEnabled?: boolean,
  tertiaryEnabled?: boolean,
  primaryButtonText?: string,
  secondaryButtonText?: string,
  tertiaryButtonText?: string,
  primaryType?: string,
  secondaryType?: string,
  tertiaryType?: string,
  primaryStyle?: string,
  secondaryStyle?: string,
  tertiaryStyle?: string,
  primaryDisabled?: boolean,
  secondaryDisabled?: boolean,
  tertiaryDisabled?: boolean,
  primaryButtonIcon?: any[] | null,
  secondaryButtonIcon?: any[] | null,
  tertiaryButtonIcon?: any[] | null,
}

const props = withDefaults(defineProps<ActionToolbarProps>(), {
  componentState: SaveStates.Blank,
  successMessage: 'Saved successfully!',
  errorMessage: 'An error occurred!',
  infoMessage: '',
  toolbarSize: 'col-sm-12',
  primaryEnabled: true,
  secondaryEnabled: true,
  tertiaryEnabled: false,
  primaryButtonText: 'Save',
  secondaryButtonText: 'Cancel',
  tertiaryButtonText: 'Info',
  primaryType: 'submit',
  secondaryType: 'button',
  tertiaryType: 'button',
  primaryStyle: 'success',
  secondaryStyle: 'outline-secondary',
  tertiaryStyle: 'primary',
  primaryDisabled: false,
  secondaryDisabled: false,
  tertiaryDisabled: false,
  primaryButtonIcon: null,
  secondaryButtonIcon: null,
  tertiaryButtonIcon: null,
});

const emit = defineEmits<{
  (e: 'primaryClick', evt: Event): void,
  (e: 'secondaryClick', evt: Event): void,
  (e: 'tertiaryClick', evt: Event): void,
}>();

const { currentState, isSavableStateProcessing, setSavableStateBlank } = useSavableState();

// We need an internal state, so we use a bridge as it ensures
// that parent always becomes the source of truth if it changes.
currentState.value = props.componentState;
watch(() => props.componentState, (newVal) => {
  currentState.value = newVal;
});

const getButtonType = (buttonType: string): ButtonHTMLAttributes['type'] => { return buttonType === 'submit' ? 'submit' : 'button' };
const primaryButtonType = computed(() => getButtonType(props.primaryType));
const secondaryButtonType = computed(() => getButtonType(props.secondaryType));
const tertiaryButtonType = computed(() => getButtonType(props.tertiaryType));

const isPrimaryButtonDisabled = computed(() => isSavableStateProcessing.value || props.primaryDisabled);
const isSecondaryButtonDisabled = computed(() => isSavableStateProcessing.value || props.secondaryDisabled);
const isTertiaryButtonDisabled = computed(() => isSavableStateProcessing.value || props.tertiaryDisabled);

const showMessage = computed(() => [SaveStates.Success, SaveStates.Errored, SaveStates.Info].includes(currentState.value));

const currentMessage = computed(() => {
  if (currentState.value === SaveStates.Success) {
    return props.successMessage;
  } else if (currentState.value === SaveStates.Errored) {
    return props.errorMessage;
  } else if (currentState.value === SaveStates.Info) {
    return props.infoMessage;
  } else {
    return '';
  }
});

function emitPrimaryClick(evt: Event) {
  emit('primaryClick', evt);
}
function emitSecondaryClick(evt: Event) {
  emit('secondaryClick', evt);
}
function emitTertiaryClick(evt: Event) {
  emit('tertiaryClick', evt);
}
function clearState() {
  setSavableStateBlank();
}
</script>
